import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useListOrganizations, organization } from "../lib/auth-client.ts";
import { useSession } from "../lib/session.tsx";
import { STORAGE_KEYS } from "@currencycore/shared";
import { ToastProvider } from "../components/Toast.tsx";
import { getOrgPlans } from "../lib/api.ts";

// The audit-log nav item, injected after SSO only for entitled orgs.
const AUDIT_NAV_ITEM = {
  label: "Audit log",
  to: "audit",
  end: false,
  icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" /><path d="M9 13h6" /><path d="M9 17h6" /><path d="M9 9h1" />
    </svg>
  ),
};

// ---------------------------------------------------------------------------
// Nav items matching the mockup sidebar
// ---------------------------------------------------------------------------

const NAV_ITEMS = [
  {
    label: "Usage",
    to: "",
    end: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 3v18h18" />
        <path d="M7 16l4-4 4 4 5-5" />
      </svg>
    ),
  },
  {
    label: "API Keys",
    to: "developer",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3m-3.5 3.5L19 4" />
      </svg>
    ),
  },
  {
    label: "Playground",
    to: "playground",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  {
    label: "Team",
    to: "team",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  {
    label: "Single Sign-On",
    to: "sso",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
  {
    label: "AI Providers",
    to: "ai-providers",
    end: false,
    // CPU/chip — reads as an AI model/provider, distinct from the API-key icon.
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="6" y="6" width="12" height="12" rx="2" />
        <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
        <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
      </svg>
    ),
  },
  {
    label: "Organizations",
    to: "organizations",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M9 8h1" /><path d="M9 12h1" /><path d="M9 16h1" />
        <path d="M14 8h1" /><path d="M14 12h1" /><path d="M14 16h1" />
        <path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" />
      </svg>
    ),
  },
  {
    label: "Billing",
    to: "billing",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
      </svg>
    ),
  },
  {
    label: "Plans",
    to: "plans",
    end: false,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 3 12V4a1 1 0 0 1 1-1h8a2 2 0 0 1 1.4.6l6.2 6.2a2 2 0 0 1 0 2.8z" />
        <circle cx="7.5" cy="7.5" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
];

// ---------------------------------------------------------------------------
// Org switcher (in sidebar style)
// ---------------------------------------------------------------------------

function slugify(name: string) {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function SidebarOrgSwitcher() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { data: orgs } = useListOrganizations();
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const activeOrg = orgs?.find((o) => o.id === orgId);

  function selectOrg(id: string) {
    localStorage.setItem(STORAGE_KEYS.lastOrgId, id);
    navigate(`/dashboard/${id}`, { replace: true });
    setOpen(false);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = createName.trim();
    if (!trimmed) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const res = await organization.create({ name: trimmed, slug: slugify(trimmed) });
      if (res?.error) { setCreateError(res.error.message ?? "Could not create organization."); return; }
      const id = res?.data?.id;
      if (!id) throw new Error("Could not create organization.");
      localStorage.setItem(STORAGE_KEYS.lastOrgId, id);
      setShowCreate(false);
      setCreateName("");
      navigate(`/dashboard/${id}`, { replace: true });
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Could not create organization.");
    } finally {
      setCreateLoading(false);
    }
  }

  return (
    <div className="org-switch">
      <button
        className="org-trigger"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`org-avatar${activeOrg ? " is-accent" : ""}`}>
          {activeOrg?.name?.[0]?.toUpperCase() ?? "?"}
        </span>
        <span className="org-meta">
          <span className="name">{activeOrg?.name ?? "Select org"}</span>
        </span>
        <svg className="org-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <div className={`org-menu${open ? " open" : ""}`}>
        <div className="org-menu-label">Workspaces</div>
        {(orgs ?? []).map((org) => (
          <button key={org.id} className="org-item" onClick={() => selectOrg(org.id)}>
            <span className={`org-avatar${org.id === orgId ? " is-accent" : ""}`}>
              {org.name[0]?.toUpperCase() ?? "?"}
            </span>
            <span className="name">{org.name}</span>
            {org.id === orgId && (
              <svg className="check" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            )}
          </button>
        ))}
        <div className="org-divider" />
        <button
          className="org-item org-create"
          onClick={() => { setOpen(false); setShowCreate(true); }}
        >
          <span className="org-avatar is-accent">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          <span className="name">Create organization</span>
        </button>
      </div>

      {/* Click-outside to close */}
      {open && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 55 }}
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Create org modal */}
      {showCreate && createPortal(
        <div
          className="modal-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowCreate(false); setCreateError(null); setCreateName(""); } }}
        >
          <div className="modal-box animate-fade-in">
            <div className="modal-head" style={{ padding: "20px 22px 0" }}>
              <div>
                <h3 style={{ fontSize: "17px" }}>Create organization</h3>
                <p style={{ fontSize: "13px", color: "var(--text-subtle)", marginTop: "4px" }}>You'll be the owner. Invite teammates after.</p>
              </div>
              <button
                className="btn btn-icon btn-ghost"
                onClick={() => { setShowCreate(false); setCreateError(null); setCreateName(""); }}
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            {createError && (
              <div className="error-banner">{createError}</div>
            )}
            <form onSubmit={handleCreate}>
              <div className="modal-body-pad">
                <div className="field">
                  <label htmlFor="newOrgName" className="form-label">Organization name</label>
                  <input
                    id="newOrgName"
                    className="input"
                    type="text"
                    required
                    autoFocus
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    placeholder="Acme Corp"
                  />
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-ghost" onClick={() => { setShowCreate(false); setCreateError(null); setCreateName(""); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={createLoading}>
                  {createLoading ? <><span className="spinner" />Creating…</> : "Create organization"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

export function DashboardLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const { data: orgs } = useListOrganizations();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [auditEnabled, setAuditEnabled] = useState(false);

  // Resolve the audit-log entitlement so the nav item is shown only to orgs
  // that have it (plan default + admin override, computed server-side).
  useEffect(() => {
    let alive = true;
    setAuditEnabled(false);
    if (!orgId) return;
    getOrgPlans(orgId)
      .then((p) => { if (alive) setAuditEnabled(!!p.current.auditLogsEnabled); })
      .catch(() => { if (alive) setAuditEnabled(false); });
    return () => { alive = false; };
  }, [orgId]);

  const navItems = useMemo(() => {
    if (!auditEnabled) return NAV_ITEMS;
    const items = [...NAV_ITEMS];
    const ssoIdx = items.findIndex((i) => i.to === "sso");
    items.splice(ssoIdx >= 0 ? ssoIdx + 1 : items.length, 0, AUDIT_NAV_ITEM);
    return items;
  }, [auditEnabled]);

  // Org guard: if the URL's :orgId isn't one this user belongs to — a stale or
  // malformed link like `/dashboard/billing` (which the router reads as
  // orgId="billing") — bounce to the base, which re-resolves a valid org or
  // shows org selection. Clear a stale sticky cache first so it can't loop back.
  useEffect(() => {
    if (Array.isArray(orgs) && orgId && !orgs.some((o) => o.id === orgId)) {
      if (localStorage.getItem(STORAGE_KEYS.lastOrgId) === orgId) {
        localStorage.removeItem(STORAGE_KEYS.lastOrgId);
      }
      navigate("/dashboard", { replace: true });
    }
  }, [orgs, orgId, navigate]);

  const activeOrgName = orgs?.find((o) => o.id === orgId)?.name ?? orgId ?? "";
  const userName = session?.user.name ?? "";
  const userEmail = session?.user.email ?? "";
  const userInitials = userName?.[0]?.toUpperCase() ?? "U";

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
        {/* Brand */}
        <div className="brand">
          <img className="brand-logo" src="/logo.webp" alt="" width="26" height="26" />
          <span className="brand-name">CurrencyCore</span>
        </div>

        {/* Org switcher */}
        <SidebarOrgSwitcher />

        {/* Nav */}
        <div className="nav-label">Navigation</div>
        <nav className="nav">
          {navItems.map((item) => (
            <NavLink
              key={item.label}
              to={`/dashboard/${orgId ?? ""}${item.to ? `/${item.to}` : ""}`}
              end={item.end}
              className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}
              onClick={() => setSidebarOpen(false)}
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-spacer" />

        {/* User card */}
        <button
          className="user-card"
          onClick={() => navigate(`/dashboard/${orgId ?? ""}/profile`)}
          title="Account settings"
        >
          <span className="avatar">{userInitials}</span>
          <span className="user-meta">
            <span className="name">{userName}</span>
            <span className="email">{userEmail}</span>
          </span>
          <svg className="kebab" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </aside>

      {/* Mobile scrim */}
      {sidebarOpen && (
        <div className="scrim-mobile show" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      {/* Main */}
      <div className="main">
        {/* Topbar */}
        <header className="topbar">
          <button
            className="btn btn-icon btn-ghost hamburger"
            aria-label="Open menu"
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          <div className="crumb">
            <span className="here">{activeOrgName}</span>
          </div>

          <div className="topbar-right">
            <a
              href="https://currency-core.com/docs/getting-started"
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-ghost"
            >
              Docs
            </a>
          </div>
        </header>

        {/* Page content */}
        <div className="content">
          <div className="content-inner">
            <Outlet />
            <footer className="app-footer">
              <span>© {new Date().getFullYear()} CurrencyCore</span>
              <a href="mailto:support@currency-core.com">Email support</a>
            </footer>
          </div>
        </div>
      </div>

      {/* Global toast layer */}
      <ToastProvider />
    </div>
  );
}
